/* src/pages/OnboardingWizard.jsx
 * 5-screen URL-first onboarding wizard.
 *
 * Screen 1 — "What's your website?" → scrape → auto-build form → live preview
 * Screen 2 — "Your business knowledge" → confirm scraped KB
 * Screen 3 — "Connect & verify tracking" → TrackingConfig
 * Screen 4 — "Who gets the lead?" → RecipientsEditor
 * Screen 5 — "Go live" → embed snippet + optional chat
 *
 * State is persisted to sessionStorage (formId + step) and
 * users/{uid}.wizardFormId (cross-device resume) after Screen 1 creates the form.
 *
 * To start fresh: click "↻ Start over" (shown when a form is in progress),
 * or navigate to /onboarding?fresh=1. Neither deletes existing form/KB docs.
 */

import React, { useReducer, useEffect, useState, useRef } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, updateDoc, addDoc, collection, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { websiteFromEmail } from '../utils/signupAttribution';
import { scrapeWebsite, verifyApi, leadApi } from '../utils/api';
import { createWizardForm, getButtonTextColor } from '../utils/formUtils';
import TrackingConfig from '../components/TrackingConfig';
import RecipientsEditor from '../components/RecipientsEditor';
import EmbedSettings from '../components/EmbedSettings';
import KbAssistantPanel from '../components/formbuilder/KbAssistantPanel';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STEPS = [
  { num: 1, label: 'Your website' },
  { num: 2, label: 'Knowledge base' },
  { num: 3, label: 'Tracking' },
  { num: 4, label: 'Notifications' },
  { num: 5, label: 'Go live' },
];

const SESSION_KEY = 'smartforms_wizard';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseBusinessName(context) {
  // Prefer og:site_name (clean brand) over the full SEO page title
  const siteNameMatch = context.match(/^Site Name:\s*(.+)/m);
  if (siteNameMatch) return siteNameMatch[1].trim();
  const titleMatch = context.match(/^Title:\s*(.+)/m);
  return titleMatch ? titleMatch[1].trim() : '';
}

function parseBrandColor(context) {
  const m = context.match(/^Brand Color:\s*(#[0-9a-fA-F]{3,6})/m);
  return m ? m[1].toLowerCase() : null;
}

function hostFromUrl(url) {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return ''; }
}

function inferStep(formData) {
  if (!formData.settings?.knowledgeBaseId) return 2;
  if (!formData.trackingPixels?.length) return 3;
  if (!formData.settings?.recipients?.length) return 4;
  return 5;
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

const initialState = {
  step: 1,
  url: '',
  businessName: '',
  brandColor: null,
  scrapedContext: '',
  formId: null,
  kbId: null,
  aiReplyEnabled: true,
  trackingPixels: [],
  trackingVerification: {},
  recipients: [],
  embedSettings: null,
  scraping: false,
  savingKb: false,
  error: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET':
      return { ...state, ...action.payload };
    case 'ERROR':
      return { ...state, error: action.error, scraping: false, savingKb: false };
    case 'RESET':
      return { ...initialState };
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Progress bar
// ---------------------------------------------------------------------------

// Per-step status reflects REAL configuration, not just "visited":
//   done    — passed AND actually configured (green ✓)
//   skipped — passed but NOT configured, e.g. tracking skipped (amber dot, not ✓)
//   current — the active step
//   upcoming— not reached yet
function getStepStatus(num, step, state) {
  if (num === step) return 'current';
  if (num > step) return 'upcoming';
  // num < step → passed; was it actually configured?
  switch (num) {
    case 1: return state.formId ? 'done' : 'skipped';
    case 2: return state.kbId ? 'done' : 'skipped';
    case 3: return state.trackingPixels.length > 0 ? 'done' : 'skipped';
    case 4: return 'done'; // owner-fallback always notifies
    default: return 'done';
  }
}

const STATUS_COLORS = {
  done:     { circle: '#22c55e', label: '#22c55e', connector: '#22c55e' },
  skipped:  { circle: '#f59e0b', label: '#b45309', connector: '#fcd34d' },
  current:  { circle: '#1e40af', label: '#1e40af', connector: '#e5e7eb' },
  upcoming: { circle: '#e5e7eb', label: '#9ca3af', connector: '#e5e7eb' },
};

function WizardProgress({ step, state }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '2.5rem' }}>
      {STEPS.map((s, idx) => {
        const status = getStepStatus(s.num, step, state);
        const colors = STATUS_COLORS[status];
        const glyph = status === 'done' ? '✓' : status === 'skipped' ? '!' : s.num;
        return (
          <React.Fragment key={s.num}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 0 }}>
              <div style={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                background: colors.circle,
                color: status === 'upcoming' ? '#9ca3af' : 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: '0.8rem',
                flexShrink: 0,
                transition: 'background 0.3s',
              }}>
                {glyph}
              </div>
              <span style={{
                fontSize: '0.68rem',
                marginTop: '0.3rem',
                color: colors.label,
                fontWeight: status === 'current' ? 700 : 400,
                textAlign: 'center',
                lineHeight: 1.2,
              }}>
                {s.label}{status === 'skipped' ? ' (skipped)' : ''}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div style={{
                height: 2,
                flex: 1,
                background: STATUS_COLORS[getStepStatus(s.num, step, state)].connector,
                marginTop: 14,
                transition: 'background 0.3s',
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

const primaryBtn = (disabled) => ({
  padding: '0.875rem 2rem',
  background: disabled ? '#93c5fd' : '#1e40af',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
  width: '100%',
});

const secondaryBtn = {
  padding: '0.75rem 2rem',
  background: 'transparent',
  color: '#6b7280',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  fontSize: '0.95rem',
  cursor: 'pointer',
  width: '100%',
};

// ---------------------------------------------------------------------------
// Main wizard
// ---------------------------------------------------------------------------

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser, userProfile } = useAuth();
  const { selectedWorkspaceId, isOwner } = useWorkspace();
  const [state, dispatch] = useReducer(reducer, initialState);

  // Screen-5 local UI state (hoisted to avoid hooks-in-nested-fn issues)
  const [copiedEmbed, setCopiedEmbed] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [showEmbedPanel, setShowEmbedPanel] = useState(false);
  // Screen-3 conscious-skip confirm
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);

  // Screen-5 pre-launch gate state — status: 'idle' | 'running' | 'pass' | 'warn' | 'fail'
  const IDLE_GATE = { status: 'idle', detail: null };
  const [gates, setGates] = useState(() => ({
    capture:  { ...IDLE_GATE },
    spam:     { ...IDLE_GATE },
    aiReply:  { ...IDLE_GATE },
    tracking: { ...IDLE_GATE },
    chat:     { ...IDLE_GATE },
    sender:   { ...IDLE_GATE },
  }));
  const [gateExpanded, setGateExpanded] = useState({});
  const [showLaunchAnyway, setShowLaunchAnyway] = useState(false);
  // run-once-per-step-5-mount guard (mirrors TrackingConfig autoVerifyAttempted ref)
  const prelaunchRanRef = useRef(false);

  useEffect(() => {
    const fromQuery =
      searchParams.get('url') ||
      searchParams.get('website') ||
      searchParams.get('site');
    let prefill = (fromQuery && fromQuery.trim()) || websiteFromEmail(currentUser?.email || '');
    if (!prefill) {
      try {
        const stored = sessionStorage.getItem('signupPrefillSite');
        if (stored) prefill = stored.includes('://') ? stored : `https://${stored}`;
      } catch {
        /* ignore */
      }
    }
    if (prefill && !state.url) {
      dispatch({ type: 'SET', payload: { url: prefill } });
    }
  }, [searchParams, currentUser?.email, state.url]);

  // -------------------------------------------------------------------------
  // Clear wizard state without deleting form/KB docs
  // -------------------------------------------------------------------------
  async function resetWizard() {
    prelaunchRanRef.current = false;
    setGates({ capture: { ...IDLE_GATE }, spam: { ...IDLE_GATE }, aiReply: { ...IDLE_GATE }, tracking: { ...IDLE_GATE }, chat: { ...IDLE_GATE }, sender: { ...IDLE_GATE } });
    setGateExpanded({});
    setShowLaunchAnyway(false);
    sessionStorage.removeItem(SESSION_KEY);
    dispatch({ type: 'RESET' });
    try {
      if (currentUser) {
        await updateDoc(doc(db, 'users', currentUser.uid), { wizardFormId: null });
      }
    } catch {
      // Non-fatal — local state is already reset
    }
  }

  // -------------------------------------------------------------------------
  // Resume from sessionStorage / Firestore on mount
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!currentUser) return;

    // ?fresh=1 skips resume entirely and clears any stored form
    if (searchParams.get('fresh') === '1') {
      resetWizard();
      return;
    }

    if (userProfile?.onboardingCompleted) {
      return;
    }

    async function restore() {
      try {
        const stored = sessionStorage.getItem(SESSION_KEY);
        const parsed = stored ? JSON.parse(stored) : null;
        const fid = parsed?.formId;

        if (fid) {
          const snap = await getDoc(doc(db, 'forms', fid));
          if (snap.exists()) {
            const d = snap.data();
            dispatch({ type: 'SET', payload: buildStateFromForm(fid, d, parsed.step) });
            return;
          }
        }

        // Fall back to Firestore profile
        const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
        const ud = userSnap.data() || {};

        if (ud.onboardingCompleted) {
          navigate('/dashboard', { replace: true });
          return;
        }

        if (ud.wizardFormId) {
          const snap = await getDoc(doc(db, 'forms', ud.wizardFormId));
          if (snap.exists()) {
            const d = snap.data();
            dispatch({ type: 'SET', payload: buildStateFromForm(ud.wizardFormId, d, null) });
          }
        }
      } catch (e) {
        console.error('[WIZARD] Restore error:', e);
      }
    }

    restore();
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh tracking verification when entering Step 5 so the recap reflects any
  // tests that ran during Step 3 (auto-verify or manual), which persist to the form doc.
  useEffect(() => {
    if (state.step !== 5 || !state.formId) return;
    let cancelled = false;
    getDoc(doc(db, 'forms', state.formId))
      .then((snap) => {
        if (!cancelled && snap.exists()) {
          dispatch({ type: 'SET', payload: { trackingVerification: snap.data().trackingVerification || {} } });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [state.step, state.formId]);

  // Pre-launch: auto-run all gates once per mount of step 5 (ref-guarded, never double-fires on re-render)
  useEffect(() => {
    if (state.step !== 5 || !state.formId) return;
    if (prelaunchRanRef.current) return;
    prelaunchRanRef.current = true;
    runAllGates(); // eslint-disable-line react-hooks/exhaustive-deps
  }, [state.step, state.formId]); // eslint-disable-line react-hooks/exhaustive-deps

  function buildStateFromForm(formId, d, savedStep) {
    const step = savedStep && savedStep > 1 ? savedStep : inferStep(d);
    return {
      formId,
      step,
      businessName: d.name ? d.name.replace(/ Contact Form$/, '') : '',
      scrapedContext: d.settings?.businessContext || '',
      url: d.settings?.websiteUrls?.[0] || '',
      kbId: d.settings?.knowledgeBaseId || null,
      aiReplyEnabled: d.settings?.autoReply !== false,
      trackingPixels: d.trackingPixels || [],
      trackingVerification: d.trackingVerification || {},
      recipients: d.settings?.recipients || [],
      embedSettings: d.embedSettings || null,
    };
  }

  function saveSession(formId, step) {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ formId, step }));
  }

  function goTo(step) {
    if (state.formId) saveSession(state.formId, step);
    dispatch({ type: 'SET', payload: { step, error: null } });
  }

  // -------------------------------------------------------------------------
  // Screen 1 — scrape + form creation
  // -------------------------------------------------------------------------

  async function handleScrapeAndCreate() {
    const url = state.url.trim();
    if (!url) return;
    dispatch({ type: 'SET', payload: { scraping: true, error: null } });

    try {
      const result = await scrapeWebsite(url, { fullContent: true, workspaceId: selectedWorkspaceId });
      if (!result.success) throw new Error(result.error || 'Website analysis failed');

      const context = result.businessContext || '';
      const businessName = parseBusinessName(context) || hostFromUrl(url) || 'My Business';
      const brandColor = parseBrandColor(context);
      const theme = brandColor
        ? {
            primaryColor: brandColor,
            buttonColor: brandColor,
            backgroundColor: '#ffffff',
            textColor: '#333333',
            borderColor: '#dddddd',
            borderRadius: '8px',
            buttonTextColor: getButtonTextColor(brandColor),
          }
        : undefined;

      const formId = await createWizardForm(selectedWorkspaceId, {
        businessName,
        theme,
        createdBy: currentUser.uid,
      });

      saveSession(formId, 1);
      // Phase 2 K: users/{uid}.wizardFormId only on the account workspace, not client workspaces.
      if (selectedWorkspaceId === currentUser.uid) {
        await updateDoc(doc(db, 'users', currentUser.uid), { wizardFormId: formId });
      }

      // Canonical workspace-level brand name (used by replyIdentity resolvers as a
      // fallback for every form in this workspace, not just the one created here).
      if (selectedWorkspaceId && isOwner) {
        try {
          await updateDoc(doc(db, 'workspaces', selectedWorkspaceId), { businessName });
        } catch (wsError) {
          console.error('Error saving workspace businessName:', wsError);
        }
      }

      dispatch({ type: 'SET', payload: {
        scraping: false,
        businessName,
        brandColor,
        scrapedContext: context,
        formId,
      }});
    } catch (e) {
      dispatch({ type: 'ERROR', error: e.message || 'Failed to analyze website. Check the URL and try again.' });
    }
  }

  // -------------------------------------------------------------------------
  // Screen 2 — save KB
  // -------------------------------------------------------------------------

  async function handleConfirmKb() {
    dispatch({ type: 'SET', payload: { savingKb: true, error: null } });
    try {
      const kbRef = await addDoc(collection(db, 'knowledge_bases'), {
        workspaceId: selectedWorkspaceId,
        name: state.businessName,
        content: state.scrapedContext,
        isShared: false,
        formsUsing: [state.formId],
        sources: [{ type: 'website', url: state.url, addedAt: new Date() }],
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      const formSnap = await getDoc(doc(db, 'forms', state.formId));
      const existingSettings = formSnap.exists() ? (formSnap.data().settings || {}) : {};

      await updateDoc(doc(db, 'forms', state.formId), {
        settings: {
          ...existingSettings,
          knowledgeBaseId: kbRef.id,
          businessContext: state.scrapedContext,
          websiteUrls: [state.url],
          autoReply: state.aiReplyEnabled,
        },
      });

      saveSession(state.formId, 3);
      dispatch({ type: 'SET', payload: { savingKb: false, kbId: kbRef.id, step: 3 } });
    } catch (e) {
      dispatch({ type: 'ERROR', error: e.message || 'Failed to save business knowledge.' });
    }
  }

  // -------------------------------------------------------------------------
  // Screen 3 — tracking
  // -------------------------------------------------------------------------

  async function handleSaveTracking(pixels) {
    dispatch({ type: 'SET', payload: { trackingPixels: pixels } });
    if (!state.formId) return;
    try {
      await updateDoc(doc(db, 'forms', state.formId), { trackingPixels: pixels });
    } catch (e) {
      console.error('[WIZARD] Tracking save error:', e);
    }
  }

  // -------------------------------------------------------------------------
  // Screen 4 — recipients
  // -------------------------------------------------------------------------

  async function handleSaveRecipients() {
    if (!state.formId) return;
    try {
      const formSnap = await getDoc(doc(db, 'forms', state.formId));
      const existingSettings = formSnap.exists() ? (formSnap.data().settings || {}) : {};
      await updateDoc(doc(db, 'forms', state.formId), {
        settings: { ...existingSettings, recipients: state.recipients },
      });
      saveSession(state.formId, 5);
      dispatch({ type: 'SET', payload: { step: 5, error: null } });
    } catch (e) {
      dispatch({ type: 'ERROR', error: e.message || 'Failed to save recipients.' });
    }
  }

  // -------------------------------------------------------------------------
  // Screen 5 — embed
  // -------------------------------------------------------------------------

  async function handleSaveEmbedSettings(embedSettings) {
    if (!state.formId) return;
    try {
      await updateDoc(doc(db, 'forms', state.formId), { embedSettings });
      dispatch({ type: 'SET', payload: { embedSettings } });
    } catch (e) {
      console.error('[WIZARD] Embed settings save error:', e);
    }
  }

  function copyEmbedCode(code) {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedEmbed(true);
      setTimeout(() => setCopiedEmbed(false), 2000);
    });
  }

  function copyFormLink(url) {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    });
  }

  async function handleFinish() {
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        onboardingCompleted: true,
        wizardFormId: null,
      });
    } catch {
      // Non-fatal
    }
    sessionStorage.removeItem(SESSION_KEY);
    navigate('/dashboard');
  }

  // -------------------------------------------------------------------------
  // Screen 5 — pre-launch gate runners
  // -------------------------------------------------------------------------

  function setGateState(key, patch) {
    setGates(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }

  function toggleGateExpanded(key) {
    setGateExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  }

  async function runCaptureGate() {
    setGateState('capture', { status: 'running', detail: null });
    try {
      if (!selectedWorkspaceId || !state.formId) {
        setGateState('capture', { status: 'warn', detail: 'Could not verify submissions. Submit a test lead and check your leads dashboard.' });
        return;
      }
      const result = await leadApi.getWorkspaceSubmissions(selectedWorkspaceId, {
        formId: state.formId,
        limit: 1,
      });
      if (result?.error) {
        setGateState('capture', { status: 'warn', detail: 'Could not verify submissions. Submit a test lead and check your leads dashboard.' });
        return;
      }
      const count = result.stats?.total ?? (result.submissions || []).length;
      if (count > 0) {
        setGateState('capture', { status: 'pass', detail: `${count} submission${count !== 1 ? 's' : ''} found - capture confirmed.` });
      } else {
        setGateState('capture', { status: 'warn', detail: 'No submissions yet. Open your live form, submit a test lead, then click "Run check" to confirm.' });
      }
    } catch {
      setGateState('capture', { status: 'warn', detail: 'Could not verify submissions. Submit a test lead and check your leads dashboard.' });
    }
  }

  async function runSpamGate() {
    setGateState('spam', { status: 'running', detail: null });
    try {
      const website = state.url || `https://${(state.businessName || 'example').toLowerCase().replace(/[^a-z0-9]/g, '')}.com`;
      const sampleFormData = {
        name: 'Jordan Miller',
        email: 'jordan.miller@gmail.com',
        message: `Hi, I came across ${state.businessName || 'your business'} and I'm interested in working with you. Could you share more about your services, pricing, and next steps?`,
        website,
      };
      const res = await verifyApi.testSpamSample(sampleFormData, state.scrapedContext);
      const score = res.result?.score ?? 0;
      const reasons = res.result?.reasons || [];
      if (score >= 70) {
        setGateState('spam', { status: 'fail', detail: { score, reasons, message: `Sample lead scored ${score}/100 - flagged as SPAM. Enrich your business context in Step 2.` } });
      } else if (score >= 30) {
        setGateState('spam', { status: 'warn', detail: { score, reasons, message: `Sample lead scored ${score}/100 - suspicious. Real leads may be filtered. Consider enriching your business context.` } });
      } else {
        setGateState('spam', { status: 'pass', detail: { score, reasons, message: `A realistic lead scores CLEAN (${score}/100) - your real leads won't be filtered.` } });
      }
    } catch (e) {
      setGateState('spam', { status: 'fail', detail: { message: e.message || 'Spam check failed.' } });
    }
  }

  async function runAiReplyGate() {
    setGateState('aiReply', { status: 'running', detail: null });
    try {
      const res = await verifyApi.testAiReply({
        leadName: 'Jordan Miller',
        leadEmail: 'jordan.miller@gmail.com',
        leadMessage: `Hi, I came across ${state.businessName || 'your business'} and I'm interested in working with you. Could you share more about your services, pricing, and next steps?`,
        knowledgeBase: state.scrapedContext,
        workspaceId: selectedWorkspaceId,
      });
      if (res.success && res.body) {
        setGateState('aiReply', { status: 'pass', detail: { subject: res.subject, body: res.body } });
      } else {
        setGateState('aiReply', { status: 'fail', detail: { message: res.error || 'AI reply generation failed. Check your knowledge base.' } });
      }
    } catch (e) {
      setGateState('aiReply', { status: 'fail', detail: { message: e.message || 'AI reply check failed.' } });
    }
  }

  function runTrackingGate() {
    if (!state.trackingPixels.length) {
      setGateState('tracking', { status: 'warn', detail: 'Conversion tracking is not set up. Closed-deal conversions will not report to your ads.' });
      return;
    }
    const GREEN = new Set(['verified', 'offline_armed']);
    const anyGreen = state.trackingPixels.some(p => GREEN.has(state.trackingVerification?.[p.platform]?.status));
    setGateState('tracking', anyGreen
      ? { status: 'pass', detail: 'At least one platform is verified and armed for closed-deal reporting.' }
      : { status: 'warn', detail: 'Tracking is configured but not yet verified. Go to Step 3 and run a test for each platform.' }
    );
  }

  async function runChatGate() {
    if (!state.kbId) {
      setGateState('chat', { status: 'warn', detail: 'Chat widget is not configured - visitors will not see a chat widget.' });
      return;
    }
    setGateState('chat', { status: 'running', detail: null });
    try {
      const res = await verifyApi.testChat({
        workspaceId: selectedWorkspaceId,
        knowledgeBaseId: state.kbId,
        message: `What does ${state.businessName || 'your business'} do and how can you help me?`,
      });
      if (res.success && res.reply) {
        setGateState('chat', { status: 'pass', detail: { reply: res.reply } });
      } else {
        setGateState('chat', { status: 'warn', detail: { message: res.error || 'Chat returned no reply. Check your knowledge base.' } });
      }
    } catch (e) {
      const is429 = e.message && (e.message.includes('429') || e.message.toLowerCase().includes('rate'));
      setGateState('chat', {
        status: 'warn',
        detail: { message: is429
          ? 'Chat is rate limited right now - this is not a failure. Wait a moment and try again.'
          : (e.message || 'Chat check failed.') },
      });
    }
  }

  async function runSenderGate() {
    setGateState('sender', { status: 'running', detail: null });
    try {
      const formSnap = await getDoc(doc(db, 'forms', state.formId));
      const settings = formSnap.exists() ? (formSnap.data().settings || {}) : {};
      const customSignature = settings.customSignature || null;
      const signatureLogoUrl = settings.signatureLogoUrl || null;
      const logoIsUnsafe = signatureLogoUrl && /\.(svg|webp)$/i.test(signatureLogoUrl);
      const issues = [];
      if (!customSignature) issues.push('No email signature - add one in Form Builder under the Emails tab.');
      if (!signatureLogoUrl) issues.push('No email logo - add one in Form Builder under the Emails tab.');
      if (logoIsUnsafe) issues.push('Logo is SVG or WebP - use PNG or JPG for email compatibility.');
      setGateState('sender', issues.length === 0
        ? { status: 'pass', detail: { customSignature, signatureLogoUrl } }
        : { status: 'warn', detail: { issues, customSignature, signatureLogoUrl } }
      );
    } catch {
      setGateState('sender', { status: 'warn', detail: { issues: ['Could not load form settings. Check your email signature in Form Builder.'] } });
    }
  }

  async function runGate(key) {
    switch (key) {
      case 'capture':  await runCaptureGate(); break;
      case 'spam':     await runSpamGate(); break;
      case 'aiReply':  await runAiReplyGate(); break;
      case 'tracking': runTrackingGate(); break;
      case 'chat':     await runChatGate(); break;
      case 'sender':   await runSenderGate(); break;
      default: break;
    }
  }

  async function runAllGates() {
    await runCaptureGate();
    await runSpamGate();
    await runAiReplyGate();
    runTrackingGate();
    await runChatGate();
    await runSenderGate();
  }

  // -------------------------------------------------------------------------
  // Render — Screen 1
  // -------------------------------------------------------------------------

  function renderScreen1() {
    // Phase A: URL input
    if (!state.formId || state.error) {
      return (
        <>
          <h2 style={headingStyle}>What's your website?</h2>
          <p style={subStyle}>
            We'll analyze it to auto-build your form, set up spam filtering, and create your AI knowledge base, all from one URL.
          </p>

          {state.error && <ErrorBanner message={state.error} />}

          <label style={labelStyle}>Website URL</label>
          <input
            type="url"
            placeholder="https://yourbusiness.com"
            value={state.url}
            onChange={e => dispatch({ type: 'SET', payload: { url: e.target.value, error: null } })}
            onKeyDown={e => { if (e.key === 'Enter') handleScrapeAndCreate(); }}
            autoFocus
            style={inputStyle}
          />
          <div style={{ height: '1.25rem' }} />
          <button
            style={primaryBtn(!state.url.trim())}
            onClick={handleScrapeAndCreate}
            disabled={!state.url.trim()}
          >
            Analyze my site →
          </button>
        </>
      );
    }

    // Phase B: Spinner
    if (state.scraping) {
      return (
        <div style={{ textAlign: 'center', padding: '2rem 0' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🔍</div>
          <h2 style={{ color: '#1e40af', marginBottom: '0.5rem' }}>Analyzing your site…</h2>
          <p style={{ color: '#6b7280', marginBottom: '2rem', fontSize: '0.95rem' }}>
            Scraping content and building your form. This takes about 10 seconds.
          </p>
          <Spinner />
        </div>
      );
    }

    // Phase C: Preview
    return (
      <>
        <h2 style={headingStyle}>
          Here's your form for{' '}
          <span style={{ color: '#1e40af' }}>{state.businessName}</span>
        </h2>
        <p style={subStyle}>
          Smart defaults applied: Email required, Name and Website optional. Customize anytime in the Form Builder.
        </p>
        {state.brandColor && (
          <p style={{ ...subStyle, display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '-0.5rem' }}>
            <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: state.brandColor, border: '1px solid #e5e7eb', flexShrink: 0 }} />
            Matched your site's colors
          </p>
        )}
        <div style={{
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          overflow: 'hidden',
          marginBottom: '1.5rem',
          background: '#f9fafb',
        }}>
          <iframe
            src={`/form/${state.formId}?embed=true`}
            title="Form preview"
            style={{ width: '100%', height: 340, border: 'none', display: 'block' }}
          />
        </div>
        {state.error && <ErrorBanner message={state.error} />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <button
            style={primaryBtn(false)}
            onClick={() => goTo(2)}
          >
            Looks good, continue
          </button>
          <button
            style={secondaryBtn}
            onClick={() => navigate(`/forms/${state.formId}/edit`)}
          >
            Customize in Form Builder
          </button>
        </div>
      </>
    );
  }

  // -------------------------------------------------------------------------
  // Render — Screen 2
  // -------------------------------------------------------------------------

  function renderScreen2() {
    return (
      <>
        <h2 style={headingStyle}>Your business knowledge</h2>
        <p style={subStyle}>
          This powers spam filtering, AI email replies, and your chat widget. Edit it to make it more accurate, then confirm.
        </p>
        {state.error && <ErrorBanner message={state.error} />}

        <label style={labelStyle}>Business name</label>
        <input
          type="text"
          value={state.businessName}
          onChange={e => dispatch({ type: 'SET', payload: { businessName: e.target.value } })}
          style={{ ...inputStyle, marginBottom: '1rem' }}
        />

        <label style={labelStyle}>
          Business context{' '}
          <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: '0.82rem' }}>
            ({state.scrapedContext.length.toLocaleString()} chars)
          </span>
        </label>
        <textarea
          value={state.scrapedContext}
          onChange={e => dispatch({ type: 'SET', payload: { scrapedContext: e.target.value } })}
          rows={10}
          style={{
            ...inputStyle,
            fontFamily: 'inherit',
            resize: 'vertical',
            lineHeight: 1.5,
            fontSize: '0.85rem',
            marginBottom: '1.5rem',
          }}
        />

        {state.scrapedContext.trim() ? (
          <KbAssistantPanel
            variant="wizard"
            kbContent={state.scrapedContext}
            scrapedPageUrls={state.url ? [state.url] : []}
          />
        ) : null}

        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.6rem',
            background: '#f0f4ff',
            border: '1px solid #c7d2fe',
            borderRadius: '8px',
            padding: '0.9rem 1rem',
            marginBottom: '1.5rem',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={state.aiReplyEnabled}
            onChange={e => dispatch({ type: 'SET', payload: { aiReplyEnabled: e.target.checked } })}
            style={{ marginTop: '0.15rem', width: 16, height: 16, flexShrink: 0, cursor: 'pointer' }}
          />
          <span style={{ fontSize: '0.9rem', color: '#374151', lineHeight: 1.5 }}>
            <strong>AI will reply to new leads automatically</strong> using this knowledge base.
            You can change this anytime.
          </span>
        </label>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <button
            style={primaryBtn(state.savingKb)}
            onClick={handleConfirmKb}
            disabled={state.savingKb}
          >
            {state.savingKb ? 'Saving…' : 'Confirm and continue'}
          </button>
          <button style={secondaryBtn} onClick={() => goTo(1)}>← Back</button>
        </div>
      </>
    );
  }

  // -------------------------------------------------------------------------
  // Render — Screen 3
  // -------------------------------------------------------------------------

  function renderScreen3() {
    return (
      <>
        <h2 style={headingStyle}>Connect & verify tracking</h2>
        <p style={subStyle}>
          This is your edge: verified conversion tracking is what competitors can't match.
          Connect at least one platform now, or skip and come back later.
        </p>
        {state.error && <ErrorBanner message={state.error} />}

        <TrackingConfig
          formId={state.formId}
          formName={`${state.businessName} Contact Form`}
          trackingPixels={state.trackingPixels}
          trackingVerification={state.trackingVerification}
          autoVerify
          onSave={handleSaveTracking}
        />

        {showSkipConfirm && (
          <div style={{
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: '8px',
            padding: '1rem 1.25rem',
            marginTop: '2rem',
            color: '#92400e',
          }}>
            <p style={{ margin: '0 0 0.85rem', fontSize: '0.9rem', lineHeight: 1.5 }}>
              Skip tracking? Closed-deal conversions won't report to your ads. This is the
              part competitors can't do.
            </p>
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
              <button
                onClick={() => { setShowSkipConfirm(false); goTo(4); }}
                style={{
                  padding: '0.6rem 1.1rem',
                  background: '#b45309',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Skip anyway
              </button>
              <button
                onClick={() => setShowSkipConfirm(false)}
                style={{
                  padding: '0.6rem 1.1rem',
                  background: 'white',
                  color: '#92400e',
                  border: '1px solid #fcd34d',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Add tracking
              </button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '2rem' }}>
          <button
            style={primaryBtn(false)}
            onClick={() => goTo(4)}
          >
            Continue
          </button>
          <button style={secondaryBtn} onClick={() => setShowSkipConfirm(true)}>Skip for now</button>
          <button style={secondaryBtn} onClick={() => goTo(2)}>← Back</button>
        </div>
      </>
    );
  }

  // -------------------------------------------------------------------------
  // Render — Screen 4
  // -------------------------------------------------------------------------

  function renderScreen4() {
    return (
      <>
        <h2 style={headingStyle}>Who gets the lead?</h2>
        <p style={subStyle}>
          Your account always gets the daily digest. Immediate alerts go to whoever the lead is assigned to — you, until a lead is assigned to someone else. Extra addresses get the same immediate alerts.
        </p>
        {state.error && <ErrorBanner message={state.error} />}

        <RecipientsEditor
          recipients={state.recipients}
          ownerEmail={currentUser?.email || ''}
          onChange={recipients => dispatch({ type: 'SET', payload: { recipients } })}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1.5rem' }}>
          <button style={primaryBtn(false)} onClick={handleSaveRecipients}>
            Save & continue
          </button>
          <button style={secondaryBtn} onClick={() => goTo(3)}>← Back</button>
        </div>
      </>
    );
  }

  // -------------------------------------------------------------------------
  // Render — Screen 5
  // -------------------------------------------------------------------------

  function renderScreen5() {
    const origin = window.location.origin;
    // Closing </script> tag assembled at runtime to avoid ESLint no-useless-escape
    const scriptClose = ['<', '/script>'].join('');
    const embedCode = `<script src="${origin}/embed.js" data-form-id="${state.formId}"></script>\n<div id="smartform-${state.formId}" style="width:100%;max-width:600px;margin:0 auto;"></div>\n<script>\n  (function(){\n    var c=document.getElementById('smartform-${state.formId}');\n    if(c){var i=document.createElement('iframe');i.src='${origin}/form/${state.formId}?embed=true';i.style='width:100%;border:none;display:block;';i.scrolling='no';c.appendChild(i);}\n  })();\n${scriptClose}`;

    // Share link uses a configurable branded origin (set REACT_APP_PUBLIC_FORM_ORIGIN in .env);
    // falls back to the current origin so it works out of the box on any hosting domain.
    const shareOrigin = process.env.REACT_APP_PUBLIC_FORM_ORIGIN || origin;
    const liveFormUrl = `${shareOrigin}/form/${state.formId}`;

    const hasTracking = state.trackingPixels.length > 0;
    const GREEN_VERIFIED = new Set(['verified', 'offline_armed']);
    const trackingVerified = hasTracking && state.trackingPixels.some(
      (p) => GREEN_VERIFIED.has(state.trackingVerification?.[p.platform]?.status)
    );

    // Pre-launch computed state
    const BLOCKING_GATES = ['spam', 'aiReply'];
    const blockingPending = BLOCKING_GATES.some(k => ['idle', 'running'].includes(gates[k].status));
    const anyBlockingFailed = BLOCKING_GATES.some(k => gates[k].status === 'fail');
    const goDashboardDisabled = blockingPending || anyBlockingFailed;
    const primaryLabel = blockingPending
      ? 'Run the checks to finish'
      : anyBlockingFailed
      ? 'Fix the failed checks above'
      : "I'm done, go to dashboard";

    const gatesDoneCount = Object.values(gates).filter(g => !['idle', 'running'].includes(g.status)).length;
    const gatesPassedCount = Object.values(gates).filter(g => g.status === 'pass').length;

    // Chip appearance per gate status
    const CHIP = {
      idle:    { bg: '#f3f4f6', color: '#6b7280', glyph: '○' },
      running: { bg: '#dbeafe', color: '#1e40af', glyph: '…' },
      pass:    { bg: '#dcfce7', color: '#16a34a', glyph: '✓' },
      warn:    { bg: '#fef9c3', color: '#b45309', glyph: '⚠' },
      fail:    { bg: '#fee2e2', color: '#dc2626', glyph: '✗' },
    };

    const GATE_ROWS = [
      { key: 'capture', label: 'Form captures a lead' },
      { key: 'spam',    label: 'Spam protection passes real leads' },
      { key: 'aiReply', label: 'AI reply works' },
      { key: 'tracking', label: 'Conversion tracking' },
      { key: 'chat',    label: state.kbId ? 'AI chat works' : 'AI chat (optional - not configured)' },
      { key: 'sender',  label: 'Sender + branding configured' },
    ];

    return (
      <>
        <h2 style={headingStyle}>You're ready to go live</h2>

        {/* Hero: instant zero-code path — open and share the live hosted form */}
        <div style={{ marginBottom: '1.75rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '0.85rem' }}>
            <button
              style={primaryBtn(false)}
              onClick={() => window.open(liveFormUrl, '_blank', 'noopener,noreferrer')}
            >
              Open your live form →
            </button>
            <button
              onClick={() => copyFormLink(liveFormUrl)}
              style={{
                ...secondaryBtn,
                color: copiedLink ? '#22c55e' : '#1e40af',
                borderColor: copiedLink ? '#22c55e' : '#c7d2fe',
              }}
            >
              {copiedLink ? '✓ Link copied!' : 'Copy form link'}
            </button>
          </div>
          <p style={{ fontSize: '0.88rem', color: '#6b7280', margin: '0 0 0.5rem', lineHeight: 1.5 }}>
            Your form is live. Share this link in your link-in-bio, email signature, or an ad - no website needed.
          </p>
          <p style={{ fontSize: '0.88rem', margin: 0 }}>
            <a
              href={liveFormUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#1e40af', textDecoration: 'underline' }}
            >
              Submit a test lead and watch it land in your inbox →
            </a>
          </p>
          <p style={{ fontSize: '0.88rem', color: '#6b7280', margin: '0.75rem 0 0', lineHeight: 1.5 }}>
            Want leads to text you?{' '}
            <Link to="/text-messaging" style={{ color: '#1e40af', textDecoration: 'underline' }}>
              Add texting
            </Link>{' '}
            after you go live.
          </p>
        </div>

        {/* Tracking nudge */}
        {!hasTracking && (
          <div style={{
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: '8px',
            padding: '0.9rem 1rem',
            marginBottom: '1.5rem',
            color: '#92400e',
            fontSize: '0.9rem',
            lineHeight: 1.5,
          }}>
            ⚠ Conversion tracking isn't set up yet: closed deals won't report back to your ads.{' '}
            <button
              onClick={() => goTo(3)}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: '#92400e',
                fontWeight: 700,
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '0.9rem',
              }}
            >
              Set up tracking
            </button>
          </div>
        )}

        {/* Configured-but-unverified nudge */}
        {hasTracking && !trackingVerified && (
          <div style={{
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: '8px',
            padding: '0.9rem 1rem',
            marginBottom: '1.5rem',
            color: '#92400e',
            fontSize: '0.9rem',
            lineHeight: 1.5,
          }}>
            ⚠ Run a quick test on Step 3 to confirm your tracking fires.{' '}
            <button
              onClick={() => goTo(3)}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: '#92400e',
                fontWeight: 700,
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '0.9rem',
              }}
            >
              Run test
            </button>
          </div>
        )}
        {state.error && <ErrorBanner message={state.error} />}

        {/* Add it to your website — guided platform picker leads, raw snippet below */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', color: '#374151', fontWeight: 700 }}>
            Add it to your website
          </h4>

          <button
            onClick={() => setShowEmbedPanel(!showEmbedPanel)}
            style={{
              ...secondaryBtn,
              marginBottom: '1rem',
              color: '#1e40af',
              borderColor: '#c7d2fe',
              textAlign: 'left',
            }}
          >
            {showEmbedPanel ? '▲ Hide embed guide' : '▼ WordPress, Squarespace, Wix, Shopify and more →'}
          </button>

          {showEmbedPanel && (
            <EmbedSettings
              formId={state.formId}
              embedSettings={state.embedSettings}
              onSave={handleSaveEmbedSettings}
              onClose={() => setShowEmbedPanel(false)}
            />
          )}

          {/* Raw embed snippet */}
          <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
            <div style={{
              background: '#f1f5f9',
              borderRadius: '8px',
              padding: '1.25rem',
              paddingRight: '6rem',
            }}>
              <pre style={{
                margin: 0,
                fontSize: '0.78rem',
                fontFamily: 'monospace',
                color: '#334155',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}>
                {embedCode}
              </pre>
            </div>
            <button
              onClick={() => copyEmbedCode(embedCode)}
              style={{
                position: 'absolute',
                top: '0.75rem',
                right: '0.75rem',
                padding: '0.4rem 0.85rem',
                background: copiedEmbed ? '#22c55e' : '#1e40af',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                fontSize: '0.78rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
            >
              {copiedEmbed ? 'Copied!' : 'Copy'}
            </button>
          </div>

          <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 0 }}>
            Not the developer?{' '}
            <a
              href={`mailto:?subject=Add gocta.ai to ${state.businessName}&body=Please add this snippet to the website:%0A%0A${encodeURIComponent(embedCode)}`}
              style={{ color: '#1e40af', textDecoration: 'underline' }}
            >
              Email it to your developer
            </a>
          </p>
        </div>

        {/* Chat one-click */}
        {state.kbId && (
          <div style={{
            background: '#f0f4ff',
            border: '1px solid #c7d2fe',
            borderRadius: '8px',
            padding: '1.25rem',
            marginBottom: '1.5rem',
          }}>
            <h4 style={{ margin: '0 0 0.4rem', color: '#3730a3', fontSize: '0.95rem' }}>
              Add AI chat to your site too
            </h4>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#4b5563' }}>
              Uses the <strong>{state.businessName}</strong> knowledge base we just built. One-click setup.
            </p>
            <button
              onClick={() => navigate(`/chat?kbId=${state.kbId}`)}
              style={{
                padding: '0.625rem 1.25rem',
                background: '#4f46e5',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Set up AI chat →
            </button>
          </div>
        )}

        {/* Pre-launch checks panel */}
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', marginBottom: '1.5rem', overflow: 'hidden' }}>
          <div style={{ padding: '0.8rem 1.25rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#111', fontWeight: 700 }}>Pre-launch checks</h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontSize: '0.8rem', color: gatesDoneCount === 6 ? (anyBlockingFailed ? '#dc2626' : '#16a34a') : '#6b7280' }}>
                {gatesDoneCount === 6
                  ? (anyBlockingFailed ? 'Some checks failed' : `${gatesPassedCount}/6 passed`)
                  : `${gatesDoneCount}/6 complete`}
              </span>
              <button
                onClick={() => { prelaunchRanRef.current = false; runAllGates(); }}
                style={{ padding: '0.3rem 0.7rem', background: '#1e40af', color: 'white', border: 'none', borderRadius: '5px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}
              >
                Re-run all
              </button>
            </div>
          </div>
          <div style={{ padding: '0.6rem 1.25rem 0.75rem' }}>
            {GATE_ROWS.map(({ key, label }) => {
              const gate = gates[key];
              const { status, detail } = gate;
              const chip = CHIP[status] || CHIP.idle;
              const expanded = !!gateExpanded[key];

              // One-line summary shown under the label (collapsed view)
              let statusLine = null;
              if (status !== 'idle' && status !== 'running') {
                if (key === 'capture') statusLine = typeof detail === 'string' ? detail : null;
                else if (key === 'spam' && detail) statusLine = detail.message || null;
                else if (key === 'aiReply' && detail) statusLine = detail.subject ? `Subject: "${detail.subject}"` : (detail.message || null);
                else if (key === 'tracking') statusLine = typeof detail === 'string' ? detail : null;
                else if (key === 'chat' && detail) statusLine = detail.reply ? `Chat replied (${detail.reply.length} chars)` : (detail.message || null);
                else if (key === 'sender' && detail) statusLine = detail.issues?.length ? detail.issues[0] : 'Signature and logo confirmed.';
              }

              return (
                <div key={key} style={{ marginBottom: '0.45rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', padding: '0.45rem 0.6rem', borderRadius: '6px', background: status !== 'idle' ? chip.bg + '44' : 'transparent' }}>
                    <span style={{ fontSize: '0.75rem', padding: '0.1rem 0.4rem', borderRadius: '4px', background: chip.bg, color: chip.color, fontWeight: 700, flexShrink: 0, minWidth: '1.5rem', textAlign: 'center' }}>
                      {chip.glyph}
                    </span>
                    <span style={{ flex: 1, fontSize: '0.87rem', color: '#374151', fontWeight: 500 }}>{label}</span>
                    {detail && status !== 'running' && (
                      <button onClick={() => toggleGateExpanded(key)} style={{ background: 'none', border: 'none', color: '#1e40af', cursor: 'pointer', fontSize: '0.77rem', padding: '0 0.2rem', textDecoration: 'underline', flexShrink: 0 }}>
                        {expanded ? 'hide' : 'details'}
                      </button>
                    )}
                    <button
                      onClick={() => runGate(key)}
                      disabled={status === 'running'}
                      style={{ padding: '0.22rem 0.6rem', background: status === 'running' ? '#e5e7eb' : '#1e40af', color: status === 'running' ? '#9ca3af' : 'white', border: 'none', borderRadius: '4px', fontSize: '0.74rem', fontWeight: 600, cursor: status === 'running' ? 'not-allowed' : 'pointer', flexShrink: 0 }}
                    >
                      {status === 'running' ? 'Checking' : 'Run check'}
                    </button>
                  </div>
                  {statusLine && !expanded && (
                    <p style={{ margin: '0.15rem 0 0 2.2rem', fontSize: '0.79rem', color: chip.color, lineHeight: 1.4 }}>{statusLine}</p>
                  )}
                  {expanded && detail && (
                    <div style={{ margin: '0.35rem 0 0.2rem 2.2rem', padding: '0.7rem', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.81rem', color: '#374151', lineHeight: 1.5 }}>
                      {key === 'spam' && (
                        <>
                          <p style={{ margin: '0 0 0.3rem' }}>Score: <strong>{detail.score}/100</strong> ({detail.score < 30 ? 'CLEAN' : detail.score < 70 ? 'SUSPICIOUS' : 'SPAM'})</p>
                          {detail.message && <p style={{ margin: '0 0 0.3rem', color: chip.color }}>{detail.message}</p>}
                          {detail.reasons?.length > 0 && (
                            <ul style={{ margin: 0, paddingLeft: '1.2rem', color: '#6b7280' }}>
                              {detail.reasons.map((r, i) => <li key={i}>{r}</li>)}
                            </ul>
                          )}
                        </>
                      )}
                      {key === 'aiReply' && detail.subject && (
                        <>
                          <p style={{ margin: '0 0 0.4rem', fontWeight: 600, color: '#111' }}>{detail.subject}</p>
                          <p style={{ margin: 0, whiteSpace: 'pre-wrap', color: '#4b5563' }}>{detail.body}</p>
                        </>
                      )}
                      {key === 'aiReply' && !detail.subject && detail.message && (
                        <p style={{ margin: 0, color: '#dc2626' }}>{detail.message}</p>
                      )}
                      {key === 'chat' && detail.reply && (
                        <p style={{ margin: 0 }}>{detail.reply}</p>
                      )}
                      {key === 'chat' && !detail.reply && detail.message && (
                        <p style={{ margin: 0, color: chip.color }}>{detail.message}</p>
                      )}
                      {key === 'sender' && (
                        <>
                          {detail.issues?.map((iss, i) => (
                            <p key={i} style={{ margin: i > 0 ? '0.2rem 0 0' : 0, color: '#b45309' }}>{iss}</p>
                          ))}
                          {detail.signatureLogoUrl && <p style={{ margin: '0.3rem 0 0', color: '#16a34a' }}>Logo: confirmed</p>}
                          {detail.customSignature && <p style={{ margin: '0.2rem 0 0', color: '#16a34a' }}>Signature: "{detail.customSignature.substring(0, 80)}{detail.customSignature.length > 80 ? '...' : ''}"</p>}
                          <p style={{ margin: '0.45rem 0 0', color: '#6b7280', fontStyle: 'italic' }}>Replies come from your verified domain. Confirm the From name in Email settings if needed.</p>
                        </>
                      )}
                      {(key === 'capture' || key === 'tracking') && typeof detail === 'string' && (
                        <p style={{ margin: 0, color: chip.color }}>{detail}</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Completion recap */}
        <div style={{
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '1rem 1.25rem',
          marginBottom: '1.5rem',
        }}>
          <h4 style={{ margin: '0 0 0.6rem', fontSize: '0.95rem', color: '#111' }}>Your setup</h4>
          {gatesDoneCount === 6 && (
            <p style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', fontWeight: 600, color: anyBlockingFailed ? '#dc2626' : gatesPassedCount === 6 ? '#16a34a' : '#b45309' }}>
              {`Pre-launch checks: ${gatesPassedCount}/6 passed${anyBlockingFailed ? ' - see checks above' : ''}`}
            </p>
          )}
          <RecapRow label="Form created" ok />
          <RecapRow label="Knowledge base" ok={!!state.kbId} fixLabel="Add it" onFix={() => goTo(2)} />
          <RecapRow
            label={state.aiReplyEnabled ? 'AI replies on' : 'AI replies off'}
            ok={state.aiReplyEnabled}
            fixLabel="Turn on"
            onFix={() => goTo(2)}
          />
          <RecapRow
            label={
              trackingVerified
                ? 'Conversion tracking verified'
                : hasTracking
                ? 'Conversion tracking armed - run a quick test to confirm'
                : 'Conversion tracking skipped'
            }
            ok={trackingVerified}
            fixLabel={hasTracking ? 'Run test' : 'Set it up'}
            onFix={() => goTo(3)}
          />
          <RecapRow label="Notifications to your inbox" ok />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <button
            style={primaryBtn(goDashboardDisabled)}
            onClick={handleFinish}
            disabled={goDashboardDisabled}
          >
            {primaryLabel}
          </button>

          {/* Launch anyway - appears when blocking checks have failed */}
          {anyBlockingFailed && !showLaunchAnyway && (
            <button
              style={{ ...secondaryBtn, color: '#b45309', borderColor: '#fcd34d' }}
              onClick={() => setShowLaunchAnyway(true)}
            >
              Launch anyway (some checks didn't pass)
            </button>
          )}
          {showLaunchAnyway && (
            <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', padding: '1rem 1.25rem', color: '#92400e' }}>
              <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', lineHeight: 1.5 }}>
                Some checks have not passed - launch without them? Your form is live but these issues may affect lead capture or email delivery.
              </p>
              <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                <button
                  onClick={async () => {
                    const failedGates = Object.entries(gates)
                      .filter(([, g]) => g.status === 'fail' || g.status === 'warn')
                      .map(([k]) => k);
                    try {
                      await updateDoc(doc(db, 'forms', state.formId), {
                        prelaunchOverride: { at: new Date(), failedGates },
                      });
                    } catch { /* non-fatal */ }
                    handleFinish();
                  }}
                  style={{ padding: '0.6rem 1.1rem', background: '#b45309', color: 'white', border: 'none', borderRadius: '6px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' }}
                >
                  Yes, launch anyway
                </button>
                <button
                  onClick={() => setShowLaunchAnyway(false)}
                  style={{ padding: '0.6rem 1.1rem', background: 'white', color: '#92400e', border: '1px solid #fcd34d', borderRadius: '6px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' }}
                >
                  Go back and fix
                </button>
              </div>
            </div>
          )}

          <button style={secondaryBtn} onClick={() => navigate(`/forms/${state.formId}/edit`)}>
            Open full Form Builder
          </button>
          <button style={secondaryBtn} onClick={() => goTo(4)}>← Back</button>
        </div>
      </>
    );
  }

  // -------------------------------------------------------------------------
  // Root render
  // -------------------------------------------------------------------------

  const restartFresh = searchParams.get('fresh') === '1';
  if (!restartFresh && userProfile?.onboardingCompleted) {
    return <Navigate to="/dashboard" replace />;
  }

  function renderCurrentStep() {
    switch (state.step) {
      case 1: return renderScreen1();
      case 2: return renderScreen2();
      case 3: return renderScreen3();
      case 4: return renderScreen4();
      case 5: return renderScreen5();
      default: return renderScreen1();
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0f4ff 0%, #f8fafc 100%)',
      padding: '2rem 1rem 4rem',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}>
      {/* Top bar */}
      <div style={{
        width: '100%',
        maxWidth: 680,
        marginBottom: '2rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1e40af', letterSpacing: '-0.02em' }}>
          gocta.ai
        </span>
        {state.formId && (
          <button
            onClick={resetWizard}
            style={{
              background: 'none',
              border: 'none',
              color: '#9ca3af',
              cursor: 'pointer',
              fontSize: '0.9rem',
              marginRight: '0.75rem',
            }}
          >
            ↻ Start over
          </button>
        )}
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            background: 'none',
            border: 'none',
            color: '#9ca3af',
            cursor: 'pointer',
            fontSize: '0.9rem',
          }}
        >
          Skip setup →
        </button>
      </div>

      {/* Card */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '2.5rem',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        maxWidth: 680,
        width: '100%',
      }}>
        <WizardProgress step={state.step} state={state} />
        {renderCurrentStep()}
      </div>

      <p style={{ marginTop: '1.5rem', fontSize: '0.82rem', color: '#9ca3af', textAlign: 'center' }}>
        Step {state.step} of {STEPS.length} · Your progress is saved automatically
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small shared sub-components
// ---------------------------------------------------------------------------

function ErrorBanner({ message }) {
  return (
    <div style={{
      background: '#fef2f2',
      border: '1px solid #fca5a5',
      borderRadius: '6px',
      padding: '0.75rem 1rem',
      marginBottom: '1rem',
      color: '#dc2626',
      fontSize: '0.9rem',
    }}>
      {message}
    </div>
  );
}

function RecapRow({ label, ok, fixLabel, onFix }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.3rem 0',
      fontSize: '0.9rem',
    }}>
      <span style={{ color: ok ? '#22c55e' : '#f59e0b', fontWeight: 700, flexShrink: 0 }}>
        {ok ? '✓' : '⚠'}
      </span>
      <span style={{ color: ok ? '#374151' : '#b45309', flex: 1 }}>{label}</span>
      {!ok && onFix && (
        <button
          onClick={onFix}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            color: '#b45309',
            fontWeight: 600,
            textDecoration: 'underline',
            cursor: 'pointer',
            fontSize: '0.85rem',
            flexShrink: 0,
          }}
        >
          {fixLabel}
        </button>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <>
      <div style={{
        width: 44,
        height: 44,
        border: '4px solid #e5e7eb',
        borderTopColor: '#1e40af',
        borderRadius: '50%',
        animation: 'wiz-spin 0.8s linear infinite',
        margin: '0 auto',
      }} />
      <style>{`@keyframes wiz-spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}

const headingStyle = { marginTop: 0, marginBottom: '0.5rem', color: '#111', fontSize: '1.5rem', fontWeight: 700 };
const subStyle = { color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.95rem', lineHeight: 1.6 };
const labelStyle = { display: 'block', fontWeight: 600, marginBottom: '0.4rem', color: '#374151', fontSize: '0.9rem' };
const inputStyle = {
  width: '100%',
  padding: '0.875rem 1rem',
  border: '1.5px solid #e5e7eb',
  borderRadius: '8px',
  fontSize: '1rem',
  boxSizing: 'border-box',
  outline: 'none',
  display: 'block',
};
